type NetworkStateListener = () => void;

const UNSTABLE_WINDOW_MS = 30_000;
const UNSTABLE_FAILURE_THRESHOLD = 3;

let failureTimestamps: number[] = [];
const listeners = new Set<NetworkStateListener>();

function notifyListeners(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Returns `true` when recent transport failures are being tracked.
 */
export function hasRecentNetworkFailures(): boolean {
  return failureTimestamps.length > 0;
}

/**
 * Records a failed API request for unstable-connection detection.
 */
export function recordNetworkFailure(): void {
  const now = Date.now();
  failureTimestamps = failureTimestamps.filter(
    (timestamp) => now - timestamp < UNSTABLE_WINDOW_MS,
  );
  failureTimestamps.push(now);
  notifyListeners();
}

/**
 * Clears recent failure history after a successful request.
 */
export function recordNetworkSuccess(): void {
  if (failureTimestamps.length === 0) {
    return;
  }
  failureTimestamps = [];
  notifyListeners();
}

/**
 * Returns `true` when multiple transport failures occurred recently while online.
 */
export function isConnectionUnstable(): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false;
  }
  const now = Date.now();
  const recentFailures = failureTimestamps.filter(
    (timestamp) => now - timestamp < UNSTABLE_WINDOW_MS,
  );
  return recentFailures.length >= UNSTABLE_FAILURE_THRESHOLD;
}

/**
 * Subscribes to unstable-connection state changes.
 */
export function subscribeToNetworkState(
  listener: NetworkStateListener,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Resets in-memory network state (for tests).
 */
export function resetNetworkState(): void {
  failureTimestamps = [];
  notifyListeners();
}
